/* eslint-disable no-confusing-arrow */
/* eslint-disable function-paren-newline */
/* eslint-disable implicit-arrow-linebreak */
/**
 * Localization utility functions for handling localized arrays
 * Used for name, desc, and other localized fields in the new schema
 */

/**
 * Convert a string value to a localized array
 * @param {string|Array} value - The value to convert
 * @param {string} locale - The locale code (default: 'en-US')
 * @returns {Array} Localized array
 */
export const convertToLocalizedArray = (value, locale = 'en-US') => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [{ locale, value }];
};

/**
 * Get localized value from array
 * @param {Array|string} array - The localized array or string
 * @param {string} locale - The locale code (default: 'en-US')
 * @returns {string} The localized value
 */
export const getLocalizedValue = (array, locale = 'en-US') => {
  if (!array) return '';
  if (!Array.isArray(array)) return array;
  if (array.length === 0) return '';

  // Try to find exact locale match
  const exactMatch = array.find((i) => i.locale === locale);
  if (exactMatch) return exactMatch.value;

  // Try to find language match (e.g., 'ar' matches 'ar-SA', 'en' matches 'en-US')
  const lang = locale.split('-')[0];
  const langMatch = array.find((i) => i.locale?.startsWith(lang) || i.locale?.split('-')[0] === lang);
  if (langMatch) return langMatch.value;

  // Try reverse: locale starts with stored locale (e.g., stored 'ar' matches locale 'ar-SA')
  const reverseMatch = array.find((i) => locale.startsWith(i.locale?.split('-')[0] || ''));
  if (reverseMatch) return reverseMatch.value;

  // Fallback to first item
  return array[0]?.value || '';
};

/**
 * Update a localized array with a new value
 * @param {Array} array - The existing localized array
 * @param {string} locale - The locale code
 * @param {string} newValue - The new value
 * @returns {Array} Updated localized array
 */
export const updateLocalizedArray = (array, locale, newValue) => {
  if (!Array.isArray(array)) {
    return [{ locale, value: newValue }];
  }

  const exists = array.find((i) => i.locale === locale);
  if (exists) {
    return array.map((i) =>
      i.locale === locale ? { ...i, value: newValue } : i,
    );
  }

  return [...array, { locale, value: newValue }];
};

/**
 * Check if a value is a localized array
 * @param {any} value - The value to check
 * @returns {boolean} True if it's a localized array
 */
export const isLocalizedArray = (value) => {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value[0]?.locale &&
    value[0]?.value !== undefined
  );
};

/**
 * Convert multiple fields to localized arrays
 * @param {Object} obj - The object with fields to convert
 * @param {Array<string>} fields - The field names to convert
 * @param {string} locale - The locale code
 * @returns {Object} Object with converted fields
 */
export const convertFieldsToLocalized = (obj, fields, locale = 'en-US') => {
  const result = { ...obj };
  fields.forEach((field) => {
    if (result[field]) {
      result[field] = convertToLocalizedArray(result[field], locale);
    }
  });
  return result;
};

/**
 * Extract values from localized fields
 * @param {Object} obj - The object with localized fields
 * @param {Array<string>} fields - The field names to extract
 * @param {string} locale - The locale code
 * @returns {Object} Object with extracted values
 */
export const extractLocalizedValues = (obj, fields, locale = 'en-US') => {
  const result = { ...obj };
  fields.forEach((field) => {
    if (result[field]) {
      result[field] = getLocalizedValue(result[field], locale);
    }
  });
  return result;
};
